import React, {useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Switch, Route, RouteComponentProps} from 'react-router-dom';
import {ConnectedRouter, push} from 'connected-react-router';

import {history} from 'store/store';
import TemplatePage from 'containers/ChooseTemplatePage';
import LoginPage from 'containers/LoginPage';
import ResetPasswordPage from 'containers/ResetPasswordPage';
import WisbitPage from 'containers/WisbitPage';
import ManageUserPage from 'containers/ManageUserPage';
import ManageWisbitPage from 'containers/ManageWisbitPage';
import ManageOrganizationPage from 'containers/ManageOrganizationPage';

import DashboardPage from 'containers/DashboardPage';

import {findUserTypeServer} from 'containers/ManageUserPage/userType';

import {selectApp, selectError, selectRouter} from 'store/selectors';
import {LeftMenu, Background, MainContainer} from 'components';

import {ROUTE, ROUTE_PARAMS} from './constants';
import {getUser} from './redux/thunks';
import TagsPage from 'containers/TagsPage';
import ResetPasswordConfirm from 'containers/ResetPasswordPage/confirm';
import {logout} from '../../store/actions';
import {getRefreshToken} from 'utils/cookies';
import {ErrorMessage} from '../ErrorPage/types';
import {ToastContainer, toast} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as Sentry from '@sentry/react';

function App() {
  const {location} = useSelector(selectRouter);
  const {user} = useSelector(selectApp);
  const error = useSelector(selectError);

  // Only need the refresh token since we can refresh the access token.
  const authenticated = !!getRefreshToken();

  const dispatch = useDispatch();

  function showError(error: ErrorMessage) {
    const message = error.message;
    if (!message) {
      return;
    }
    toast.error(message);
  }

  const resettingPassword = location.pathname.startsWith(ROUTE.ResetPassword);

  // Only run effects when not resetting the password ()
  useEffect(() => {
    if (!resettingPassword && authenticated && user && !user.email) {
      getUser(dispatch);
    }
  }, [dispatch, user, authenticated, resettingPassword]);

  useEffect(() => {
    showError(error);
  }, [error]);

  useEffect(() => {
    if (!authenticated && !resettingPassword) {
      dispatch(push(ROUTE.Index, {from: location}));
    }
    // Disabling eslint for next line since we don't want to re-run this,
    // effect on every location change, otherwise eslint will complain
    // about missing dependencies.
    // eslint-disable-next-line
  }, [authenticated, dispatch, resettingPassword]);

  const onLogout = () => {
    dispatch(logout());
    dispatch(push(ROUTE.Index, {from: location}));
  };

  const AuthRoute = ({
    comp: Component,
    exact = true,
    path,
    ...props
  }: {
    comp: React.ComponentType<RouteComponentProps>;
    path: string;
    exact?: boolean;
  }) =>
    authenticated ? (
      <Route
        exact={exact}
        path={path}
        {...props}
        render={matchProps => (
          <Background>
            <LeftMenu
              type={findUserTypeServer(user.role)}
              onLogout={onLogout}
            />
            <MainContainer>
              <Component {...matchProps} />
            </MainContainer>
          </Background>
        )}
      />
    ) : null;

  return (
    <>
      <ToastContainer hideProgressBar={true} position={'top-center'} />
      <Sentry.ErrorBoundary>
        <ConnectedRouter history={history}>
          <Switch>
            <AuthRoute path={ROUTE.Wisbit.Edit} comp={WisbitPage} />
            <AuthRoute path={ROUTE.Wisbit.Template} comp={TemplatePage} />
            <AuthRoute path={ROUTE.Wisbit.Create} comp={WisbitPage} />
            <AuthRoute path={ROUTE.Wisbit.Index} comp={ManageWisbitPage} />
            <AuthRoute path={ROUTE.Dashboard} comp={DashboardPage} />
            <AuthRoute
              path={ROUTE.Organization}
              comp={ManageOrganizationPage}
            />
            <AuthRoute path={ROUTE.Tags} comp={TagsPage} />
            <Route
              path={ROUTE.ResetPasswordConfirm}
              component={ResetPasswordConfirm}
            />
            <Route path={ROUTE.ResetPassword} component={ResetPasswordPage} />
            <AuthRoute path={`/${ROUTE_PARAMS.type}`} comp={ManageUserPage} />
            <Route path={ROUTE.Index} component={LoginPage} />
          </Switch>
        </ConnectedRouter>
      </Sentry.ErrorBoundary>
    </>
  );
}

export default App;
